import { Headphones, Mic, MicOff, Radio, Volume2, VolumeX } from "lucide-react";
import { useEffect, useRef } from "react";
import type { PublicPlayer, VoiceChannel, VoiceSettings } from "@ma-soi/game-core";
import type { VoiceChatController } from "../hooks/useVoiceChat";
import { SeatAvatar } from "./SeatAvatar";

const CHANNEL_LABELS: Record<VoiceChannel, string> = {
  lobby: "Âm thanh phòng chờ",
  day: "Kênh dân làng",
  werewolves: "Kênh Ma Sói",
  ghost: "Kênh linh hồn",
  none: "Không có kênh thoại",
};

export const VOICE_REASON_TEXT: Record<string, string> = {
  "voice-disabled": "Voice chat đang bị tắt.",
  "phase-locked": "Micro đã được tạm khóa để giữ bí mật vai trò.",
  sleeping: "Màn đêm buông xuống. Bạn đang ngủ, micro đã được tắt.",
  "dead-listen-only": "Chỉ nghe · Bạn đã rời cuộc chơi.",
  "dead-silent": "Chỉ nghe · Kênh linh hồn đang đóng.",
  "voting-muted": "Tạm tắt khi bỏ phiếu.",
  "host-muted": "Bị chủ phòng tắt mic.",
  "host-muted-all": "Bị chủ phòng tắt mic cùng cả phòng.",
  "listen-only": "Chỉ nghe · Chủ phòng đã khóa quyền nói.",
  "channel-disabled": "Kênh thoại hiện không khả dụng trong giai đoạn này.",
  available: "Đang thì thầm.",
};

function RemoteAudio({
  stream,
  volume,
}: {
  stream: MediaStream;
  volume: number;
}) {
  const ref = useRef<HTMLAudioElement | null>(null);
  useEffect(() => {
    if (!ref.current) return;
    ref.current.srcObject = stream;
    const audio = ref.current;
    return () => { audio.srcObject = null; };
  }, [stream]);
  useEffect(() => {
    if (ref.current) ref.current.volume = volume;
  }, [volume]);
  return <audio ref={ref} autoPlay playsInline />;
}

// Luồng âm thanh có vòng đời độc lập với các bảng điều khiển đóng/mở.
export function VoiceAudioSink({ voice }: { voice: VoiceChatController }) {
  return <div className="voice-audio-sink" aria-hidden="true">
    {voice.remoteStreams.map(({ playerId, stream }) => (
      <RemoteAudio key={playerId} stream={stream} volume={voice.masterVolume * (voice.peerVolumes[playerId] ?? 1)} />
    ))}
  </div>;
}

export function VoicePanel({
  voice,
  settings,
  backgroundVolume,
  setBackgroundVolume,
  sfxVolume,
  setSfxVolume,
  players = [],
}: {
  voice: VoiceChatController;
  settings: VoiceSettings;
  backgroundVolume: number;
  setBackgroundVolume: (value: number) => void;
  sfxVolume: number;
  setSfxVolume: (value: number) => void;
  players?: PublicPlayer[];
}) {
  useEffect(() => () => voice.setPushToTalkPressed(false), [voice.setPushToTalkPressed]);
  const statusText = voice.error
    ?? (!voice.browserSupported ? "Trình duyệt của bạn chưa hỗ trợ WebRTC hoặc micro." : voice.access.reason === "available" ? "Sẵn sàng kết nối âm thanh." : VOICE_REASON_TEXT[voice.access.reason]);
  const micActive = voice.voiceEnabled && voice.access.canSpeak && voice.micEnabled && !voice.mutedBySelf;
  const primaryLabel = !voice.voiceEnabled
    ? voice.access.canSpeak ? "Bật micro" : "Bật nghe"
    : voice.access.canSpeak
      ? !voice.micEnabled ? "Bật micro" : voice.mutedBySelf ? "Mở micro" : "Tắt micro"
      : "Tắt nghe";
  const PrimaryIcon = voice.access.canSpeak
    ? micActive ? Mic : MicOff
    : voice.voiceEnabled && voice.access.canListen ? Headphones : MicOff;

  return (
    <section className={`voice-panel surface-panel voice-${voice.access.channel}`}>
      <div className="voice-panel-main">
        <button
          className={`voice-orb ${voice.canSendAudio ? "active" : ""}`}
          disabled={!voice.browserSupported || !voice.access.canListen}
          onClick={() => {
            if (!voice.voiceEnabled) void voice.startVoice();
            else if (voice.access.canSpeak && !voice.micEnabled) void voice.requestMicrophone();
            else if (voice.access.canSpeak) voice.toggleMute();
            else voice.leaveVoice();
          }}
          aria-label={primaryLabel}
          title={primaryLabel}
        >
          <PrimaryIcon className="size-6" />
          <span>{primaryLabel}</span>
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Radio className="size-4 text-amber-400" />
            <strong className="truncate text-sm text-amber-100">{CHANNEL_LABELS[voice.access.channel]}</strong>
          </div>
          <p className="mt-1 text-xs text-stone-400">{statusText}</p>
          <p className="mt-1 text-[11px] text-stone-500">
            {voice.voiceEnabled
              ? voice.access.canSpeak
                ? voice.canSendAudio ? "Đang thì thầm" : "Bạn đang im lặng"
                : "Bạn đang lắng nghe"
              : "Nhấn để tham gia âm thanh trong làng"}
          </p>
        </div>
        {voice.voiceEnabled && (
          <button className="icon-button shrink-0" onClick={voice.leaveVoice} aria-label="Rời kênh thoại" title="Rời kênh thoại">
            <VolumeX className="size-4" />
          </button>
        )}
      </div>

      {voice.participants.length > 0 && (
        <div className="voice-participant-strip" aria-label="Người trong kênh thoại">
          {voice.participants.map((participant) => {
            const player = players.find((candidate) => candidate.uid === participant.playerId);
            return (
              <span key={participant.playerId} className={participant.isSpeaking ? "speaking" : ""} title={participant.name}>
                <SeatAvatar seat={player?.seat ?? 99} avatarId={player?.avatarId} name={participant.name} muted={!participant.connected} />
              </span>
            );
          })}
        </div>
      )}

      {settings.pushToTalk && voice.voiceEnabled && voice.access.canSpeak && (
        <button
          className={`push-talk-button ${voice.pushToTalkPressed ? "active" : ""}`}
          onPointerDown={(event) => { event.currentTarget.setPointerCapture(event.pointerId); voice.setPushToTalkPressed(true); }}
          onPointerUp={() => voice.setPushToTalkPressed(false)}
          onPointerCancel={() => voice.setPushToTalkPressed(false)}
          onPointerLeave={() => voice.setPushToTalkPressed(false)}
          onKeyDown={(event) => { if (event.key === " " || event.key === "Enter") { event.preventDefault(); voice.setPushToTalkPressed(true); } }}
          onKeyUp={(event) => { if (event.key === " " || event.key === "Enter") voice.setPushToTalkPressed(false); }}
          onBlur={() => voice.setPushToTalkPressed(false)}
        >
          Giữ để nói
        </button>
      )}

      <details className="voice-details">
        <summary><Volume2 className="size-3" /> Âm lượng</summary>
        <div className="voice-sliders">
          <label>
            <span>Voice</span>
            <input type="range" min="0" max="1" step="0.01" value={voice.masterVolume} onChange={(event) => voice.setMasterVolume(Number(event.target.value))} />
          </label>
          <label>
            <span>Nhạc &amp; không khí</span>
            <input type="range" min="0" max="0.5" step="0.01" value={backgroundVolume} onChange={(event) => setBackgroundVolume(Number(event.target.value))} />
          </label>
          <label>
            <span>Hiệu ứng</span>
            <input type="range" min="0" max="1" step="0.01" value={sfxVolume} onChange={(event) => setSfxVolume(Number(event.target.value))} />
          </label>
        </div>

        {voice.remoteStreams.length > 0 && (
          <div className="voice-peer-list">
            {voice.remoteStreams.map(({ playerId }) => (
              <label key={playerId}>
                <span>{voice.playerNames.get(playerId) ?? "Người chơi"}</span>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={voice.peerVolumes[playerId] ?? 1}
                  onChange={(event) => voice.setPeerVolume(playerId, Number(event.target.value))}
                />
              </label>
            ))}
          </div>
        )}
      </details>
    </section>
  );
}
